import React, { PureComponent, Fragment } from 'react';
import { number, arrayOf, shape, string, array } from 'prop-types';
import {
  toCommaNumber,
  removeWhiteSpaceAndNewLineAndLowercase,
} from 'common/formatters';
import { PinstripePattern } from 'common/svg/PinstripePattern';
import './index.css';

/**
 *
 *
 * @export
 * @class CustomFlyout
 * @extends {PureComponent}
 */
export class CustomFlyout extends PureComponent {
  static displayName = 'CustomFlyout';

  static propTypes = {
    contracted: arrayOf(shape({ label: string, value: number })).isRequired,
    surplus: arrayOf(shape({ label: string, value: number })).isRequired,
    deficit: arrayOf(shape({ label: string, value: number })).isRequired,
    rawRevenueData: array,
    orientation: string,
    x: number,
    tooltipWidth: number,
    datum: shape({ xName: string }),
  };

  static defaultProps = {
    rawRevenueData: [],
  };

  render() {
    const { x, orientation, tooltipWidth } = this.props;

    const offset =
      orientation === 'left' ? x - (Number(tooltipWidth) + 15) : Number(x) + 15;

    return (
      <foreignObject
        className="customflyout"
        x={Math.round(offset)}
        y={Math.round(80)}
        height={200}
        width={tooltipWidth}
      >
        <div
          className="customflyout__inner"
          style={{ width: `${tooltipWidth - 10}px` }}
        >
          {this.getRevenueSection()}
          <hr />
          {this.getQuantitySection()}
        </div>
      </foreignObject>
    );
  }

  /**
   *
   *
   * @function getQuantitySection
   * @memberof CustomFlyout
   * @returns {React.Dom}
   * @public
   */
  getQuantitySection = () => {
    const {
      expectedQuantity,
      contractedQuantity,
      uncontractedQuantity,
      deficitQuantity,
    } = this.getQuantityDataItem();

    const expectedHarvestString = expectedQuantity
      ? `${toCommaNumber(expectedQuantity.toFixed())} bu`
      : '-';
    const contractedQuantityString = contractedQuantity
      ? `${toCommaNumber(contractedQuantity.toFixed())} bu`
      : '-';

    return (
      <Fragment>
        <h3>Quantity</h3>
        <dl className="customflyout__list">
          <dt>Expected Harvest</dt>
          <dd>{expectedHarvestString}</dd>
        </dl>
        <dl className="customflyout__list">
          <dt>Contracted</dt>
          <dd>{contractedQuantityString}</dd>
        </dl>
        {uncontractedQuantity != null && uncontractedQuantity > 0 && (
          <dl className="customflyout__list">
            <dt>Uncontracted</dt>
            <dd>{toCommaNumber(uncontractedQuantity.toFixed())} bu</dd>
          </dl>
        )}
        {deficitQuantity != null && deficitQuantity > 0 && (
          <dl className="customflyout__list">
            <dt>Deficit</dt>
            <dd>{toCommaNumber(deficitQuantity.toFixed())} bu</dd>
          </dl>
        )}
      </Fragment>
    );
  };

  /**
   *
   *
   * @function getQuantityDataItem
   * @memberof CustomFlyout
   * @returns {object}
   * @public
   */
  getQuantityDataItem = () => {
    const {
      rawRevenueData,
      datum: { name },
    } = this.props;

    // Convert crop name back to original form, replace line breaks with spaces
    const cropName = removeWhiteSpaceAndNewLineAndLowercase(name);

    const returnData = rawRevenueData.find(({ crop }) => {
      const varietyString = crop.variety || '';
      const cropVariety = removeWhiteSpaceAndNewLineAndLowercase(
        `${crop.name}${varietyString}`
      );
      return cropVariety === cropName;
    });

    return returnData || {};
  };

  /**
   *
   *
   * @function getRevenueSection
   * @memberof CustomFlyout
   * @returns {React.Dom}
   * @public
   */
  getRevenueSection = () => {
    const {
      contractedRevenue,
      surplusRevenue,
      deficitRevenue,
    } = this.getTooltipRevenueData();
    const totalRevenue = (contractedRevenue || 0) + (surplusRevenue || 0);
    const totalRevenueString = `$${toCommaNumber(totalRevenue.toFixed())}`;
    const contractedRevenueString = contractedRevenue
      ? `$${toCommaNumber(contractedRevenue.toFixed())}`
      : '-';

    return (
      <Fragment>
        <dl className="customflyout__list">
          <dt>
            <h3>Revenue Total</h3>
          </dt>
          <dd>{totalRevenueString}</dd>
        </dl>

        <dl className="customflyout__list">
          <dt>
            <i className="barchart__dot barchart__dot--primary" />
            Contracted
          </dt>
          <dd>{contractedRevenueString}</dd>
        </dl>
        {surplusRevenue != null && surplusRevenue > 0 && (
          <dl className="customflyout__list">
            <dt>
              <i className="barchart__dot barchart__dot--secondary" />
              Uncontracted
            </dt>
            <dd>${toCommaNumber(surplusRevenue.toFixed())}</dd>
          </dl>
        )}
        {deficitRevenue != null && deficitRevenue > 0 && (
          <dl className="customflyout__list">
            <dt>
              <i className="barchart__dot">
                <PinstripePattern />
              </i>
              Deficit
            </dt>
            <dd>${toCommaNumber(deficitRevenue.toFixed())}</dd>
          </dl>
        )}
      </Fragment>
    );
  };

  /**
   *
   *
   * @function getTooltipRevenueData
   * @memberof CustomFlyout
   * @returns {object}
   * @public
   */
  getTooltipRevenueData = () => {
    const { contracted, surplus, deficit } = this.props;

    return {
      contractedRevenue: this.getRevenueDataItem(contracted).value,
      surplusRevenue: this.getRevenueDataItem(surplus).value,
      deficitRevenue: this.getRevenueDataItem(deficit).value,
    };
  };

  /**
   *
   * @function getRevenueDataItem
   * @memberof CustomFlyout
   * @param {array} data - contracted or surplus data
   * @returns {object}
   * @public
   */
  getRevenueDataItem = data => {
    if (!data) return {};

    const {
      datum: { name },
    } = this.props;

    // Convert crop name back to original form, replace line breaks with spaces
    const cropName = removeWhiteSpaceAndNewLineAndLowercase(name);

    const returnData = data.find(item => {
      const label = removeWhiteSpaceAndNewLineAndLowercase(item.name);
      return label === cropName;
    });

    return returnData || {};
  };
}



// WEBPACK FOOTER //
// ./src/common/components/BarChart/components/CustomFlyout/index.js